import dayjs from "dayjs";
import {forecastWithRateV2} from "./Helpers";

export const forecastData = (data, fields, growthRate, numPredictions = 7, forecastType = "month") => {
    const predictions = numPredictions ? new Array(numPredictions) : [];
    // Check if fields array and growthRate object are provided
    if (!Array.isArray(fields) || fields.length === 0 || typeof growthRate !== 'object' || Object.keys(growthRate).length === 0) {
        return [];
    }

    // Check if numPredictions is a positive integer
    if (!Number.isInteger(numPredictions) || numPredictions <= 0) {
        return [];
    }

    // must have at least one data point to create prediction
    if (data.length < 1) {
        return [];
    }

    // Create a new array for predictions
    const currData = data[data.length - 1];
    const currDate = dayjs(currData?.collection_date);
    const running = {};
    let itr = 0;

    const growthRateKeyed = Object.keys(growthRate);
    // if there is no growth rate supplied, or it is undefined, just create one using simple avg
    if (!growthRateKeyed.length || growthRateKeyed.every(key => growthRate[key] === undefined)) {
        return forecastWithRateV2(data, fields, numPredictions, null, forecastType);
    }

    for (let i = 0; i < numPredictions; i++) {
        itr += 1;
        const thisDate = currDate.add(itr, forecastType);
        const nextData = {
            collection_date: thisDate.format('YYYY-MM-DDTHH:mm:ss.s[Z]'),
            month: thisDate.format('MM/YYYY'),
            isForecasted: true,
        };

        fields.forEach((field) => {
            // Use the growth rate to predict the next value
            const rate = forecastType === "month" ? growthRate[field] : growthRate[field] * 12;
            if (!running[field]) {
                running[field] = (rate || 0);
            }
            const nextValue = currData[field] + running[field] * (i + 1);
            const negativeRule = (field !== 'vm_build_capacity' && nextValue < 0);
            nextData[field] = negativeRule ? 0 : nextValue;
        });

        predictions[i] = nextData;
    }

    return predictions;
}

export const weightedGrowthRate = (values) => {
    if (!values || values.length < 2) return 0.00
    let tier1_growth = [];
    let tier2_growth = [];
    let tier3_growth = [];

    const currDate = dayjs(values[values.length - 1].date);
    const tier1_date = currDate.add(-2, 'month');
    const tier2_date = currDate.add(-4, 'month');

    const tier1_records = values.filter(f => dayjs(f.date) >= tier1_date).sort((a, b) => a.date - b.date);
    const tier2_records = values.filter(f => dayjs(f.date) >= tier2_date && f.date < tier1_date).sort((a, b) => a.date - b.date);
    const tier3_records = values.filter(f => dayjs(f.date) < tier2_date).sort((a, b) => a.date - b.date);
    tier1_records.forEach((r, i) => {
        if (tier1_records.length > i + 1) {
            const day1 = dayjs(r.date);
            const day2 = dayjs(tier1_records[i + 1].date);
            const days = day2.diff(day1, 'day');
            for (let j = days; j > 0; j--) {
                tier1_growth.push((tier1_records[i + 1].value - r.value) / days);
            }
        }
    });
    tier2_records.forEach((r, i) => {
        if (tier2_records.length > i + 1) {
            const day1 = dayjs(r.date);
            const day2 = dayjs(tier2_records[i + 1].date);
            const days = day2.diff(day1, 'day');
            for (let j = days; j > 0; j--) {
                tier2_growth.push((tier2_records[i + 1].value - r.value) / days);
            }
        }
    });
    tier3_records.forEach((r, i) => {
        if (tier3_records.length > i + 1) {
            const day1 = dayjs(r.date);
            const day2 = dayjs(tier3_records[i + 1].date);
            const days = day2.diff(day1, 'day');
            for (let j = days; j > 0; j--) {
                tier3_growth.push((tier3_records[i + 1].value - r.value) / days);
            }
        }
    });
    if (tier2_growth.length === 0) {
        tier2_growth = tier1_growth;
        tier3_growth = tier1_growth;
    } else if (tier3_growth.length === 0) {
        tier3_growth = tier2_growth;
    }
    let tier1 = 0.6 * tier1_growth.reduce((a, b) => a + b, 0) / tier1_growth.length;
    let tier2 = 0.3 * tier2_growth.reduce((a, b) => a + b, 0) / tier2_growth.length;
    let tier3 = 0.1 * tier3_growth.reduce((a, b) => a + b, 0) / tier3_growth.length;

    return tier1 + tier2 + tier3

}

export const exponentialWeightedMovingAverage = (values, alpha = 0.3) => {
    let ewma = values[0]; // Initialize with the first value
    const smoothedValues = [ewma];

    for (let i = 1; i < values.length; i++) {
        ewma = alpha * values[i] + (1 - alpha) * ewma;
        smoothedValues.push(ewma);
    }

    return smoothedValues;
}

export const ewmaGrowthRate = (values, alpha = 0.3) => {
    const smoothedValues = exponentialWeightedMovingAverage(values, alpha);
    const growthRates = [];

    for (let i = 1; i < smoothedValues.length; i++) {
        growthRates.push(Math.log(smoothedValues[i] / smoothedValues[i - 1]));
    }

    // Average growth rate
    const avgGrowthRate = growthRates.reduce((a, b) => a + b, 0) / growthRates.length;

    // Convert to percentage
    return (Math.exp(avgGrowthRate) - 1) * 100;
}


export const weightedLeastSquaresGrowth = (dates, values, lambda = 0.1) => {
    // Convert dates to numerical values (assuming they are sorted)
    const t = dates.map((_, i) => i + 1);
    const y = values.map(v => Math.log(v)); // Taking log for growth calculation

    // Compute weights
    const n = t.length;
    const weights = t.map(i => Math.exp(-lambda * (n - i)));

    // Compute weighted means
    const sumW = weights.reduce((a, b) => a + b, 0);
    const meanT = t.reduce((sum, val, i) => sum + weights[i] * val, 0) / sumW;
    const meanY = y.reduce((sum, val, i) => sum + weights[i] * val, 0) / sumW;

    // Compute weighted covariance and variance
    let num = 0, denom = 0;
    for (let i = 0; i < n; i++) {
        num += weights[i] * (t[i] - meanT) * (y[i] - meanY);
        denom += weights[i] * (t[i] - meanT) ** 2;
    }

    // Slope (growth rate per day)
    const beta1 = num / denom;

    // Convert growth rate from log scale to percentage
    const dailyGrowthRate = (Math.exp(beta1) - 1) * 100;
    return dailyGrowthRate;
}